import React, { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { Button } from "../../../../../../../../../components/Form";
import { Color } from "../../../../../../../../../components/Form/Button";
import useYupValidation from "../../../../../../../../../hooks/useYupValidation";
import { TypeOfMember } from "../../../../../../../../../util/enums/members";
import { useActions } from "../../hooks/actions";
import { AccordionCard } from "../Accordion";
import { Steps } from "../Steps";
import { AccordionDetailsWrapper, ButtonWrapper, ContentWrapper, StepsWrapper } from "./styles";

export const CreateComponent: React.FC<{
  errors: yup.ValidationError[];
  onSubmit: () => void;
  isCreating?: boolean;
}> = (props) => {
  const { t: translate } = useTranslation();
  const { errors, onSubmit, isCreating = false } = props;
  const { member, onBackToMembersTab } = useActions();

  const { fieldHasError } = useYupValidation(errors);

  const errorsOnStepTwo = useMemo(() => {
    if (member?.typeOfMember?.toLowerCase() === TypeOfMember.STAFF.toLowerCase()) {
      return (
        fieldHasError("firstName") ||
        fieldHasError("lastName") ||
        fieldHasError("status") ||
        fieldHasError("mobile")
      );
    }

    return (
      fieldHasError("firstName") ||
      fieldHasError("lastName") ||
      fieldHasError("status") ||
      fieldHasError("mobile") ||
      fieldHasError("email")
    );
  }, [member?.typeOfMember, errors]);

  const errorsOnStepThree = useMemo(() => {
    if (member?.typeOfMember?.toLowerCase() === TypeOfMember.STAFF.toLowerCase()) {
      return fieldHasError("residencyStatus");
    }

    return fieldHasError("roleId") || fieldHasError("typeOfUser");
  }, [member?.typeOfMember, errors]);

  const [isAccordionStepCollapsed, setIsAccordionStepCollapsed] = useState({
    stepOne: true,
    stepTwo: false,
    stepThree: false,
    stepFour: false,
    stepFive: false,
  });

  const steps = [
    {
      stepNumber: 1,
      title: translate("chooseTheTypeOfMember"),
      subtitle: translate("theMemberCanHaveAccessToSystemOrNot"),
      isMandatory: true,
      component: <Steps.TYPE_OF_MEMBER errors={errors} />,
      showStep: true,
      hasErrors: fieldHasError("typeOfMember") || fieldHasError("subcontractorId"),
      expanded: isAccordionStepCollapsed.stepOne,
      onChange: () =>
        setIsAccordionStepCollapsed((prev) => ({
          ...prev,
          stepOne: !isAccordionStepCollapsed.stepOne,
        })),
    },
    {
      stepNumber: 2,
      title:
        member?.typeOfMember?.toLowerCase() === TypeOfMember.USER.toLowerCase()
          ? translate("personalDetails")
          : translate("contactDetails"),
      subtitle: translate("generalInformationAboutTheMember"),
      isMandatory: true,
      component: <Steps.DETAILS errors={errors} />,
      showStep: !!member?.typeOfMember,
      hasErrors: errorsOnStepTwo,
      expanded: isAccordionStepCollapsed.stepTwo,
      onChange: () =>
        setIsAccordionStepCollapsed((prev) => ({
          ...prev,
          stepTwo: !isAccordionStepCollapsed.stepTwo,
        })),
    },
    {
      stepNumber: 3,
      title: translate("userDetails"),
      subtitle: translate("howThePersonAccessesAndInteractsWithTheSystem"),
      isMandatory: true,
      component: <Steps.USER_DETAILS errors={errors} />,
      showStep:
        !!member?.typeOfMember &&
        member?.typeOfMember.toLowerCase() === TypeOfMember.USER.toLowerCase(),
      hasErrors: errorsOnStepThree,
      expanded: isAccordionStepCollapsed.stepThree,
      onChange: () =>
        setIsAccordionStepCollapsed((prev) => ({
          ...prev,
          stepThree: !isAccordionStepCollapsed.stepThree,
        })),
    },
    {
      stepNumber: 3,
      title: translate("staffInformation"),
      subtitle: translate("specificDetailsAboutTheStaffPerson"),
      isMandatory: true,
      component: <Steps.STAFF_DETAILS errors={errors} />,
      showStep:
        !!member?.typeOfMember &&
        member?.typeOfMember.toLowerCase() === TypeOfMember.STAFF.toLowerCase(),
      hasErrors: errorsOnStepThree,
      expanded: isAccordionStepCollapsed.stepFour,
      onChange: () =>
        setIsAccordionStepCollapsed((prev) => ({
          ...prev,
          stepFour: !isAccordionStepCollapsed.stepFour,
        })),
    },
    {
      stepNumber: 4,
      title: translate("additional"),
      subtitle: "",
      isMandatory: true,
      component: <Steps.ADDITIONAL errors={errors} />,
      showStep: !!member?.typeOfMember,
      hasErrors: fieldHasError("branches"),
      expanded: isAccordionStepCollapsed.stepFive,
      onChange: () =>
        setIsAccordionStepCollapsed((prev) => ({
          ...prev,
          stepFive: !isAccordionStepCollapsed.stepFive,
        })),
    },
  ];

  return (
    <ContentWrapper>
      <StepsWrapper>
        {steps.map((step, index: number) => (
          <>
            {step?.showStep ? (
              <AccordionCard.Root
                key={index}
                hasError={step.hasErrors}
                expanded={step.expanded}
                onChange={step.onChange}
              >
                <AccordionCard.Header
                  title={step.title}
                  step={step.stepNumber}
                  subtitle={step.subtitle}
                  isMandatory={step.isMandatory}
                  hasError={step.hasErrors}
                />

                <AccordionCard.Divider />

                <AccordionDetailsWrapper>{step.component}</AccordionDetailsWrapper>
              </AccordionCard.Root>
            ) : null}
          </>
        ))}
      </StepsWrapper>

      <ButtonWrapper>
        <Button customColor={Color.TEXT_INFO} onClick={onBackToMembersTab}>
          {translate("cancel")}
        </Button>
        <Button
          customColor={Color.SUCCESS}
          loading={isCreating}
          disabled={isCreating || !member?.typeOfMember}
          onClick={onSubmit}
        >
          {translate("create")}
        </Button>
      </ButtonWrapper>
    </ContentWrapper>
  );
};
